import { useEffect, useState } from 'react'
import { api } from '../api'
import { Card, Button, Form, Input, Spin, Typography, Descriptions, message, Empty, Space, Alert } from 'antd'
import { EditOutlined, FileTextOutlined, UserOutlined } from '@ant-design/icons'
import useIsMobile from '../hooks/useIsMobile'
import { ListaDocumentos } from './documentosComun'

const { Title, Text } = Typography

const fecha = (f) => f ? new Date(f + 'T00:00:00').toLocaleDateString('es-ES') : '—'
const o = (v) => v || '—'

export default function MiPerfil() {
  const isMobile = useIsMobile()
  const [ficha, setFicha] = useState(null)
  const [documentos, setDocumentos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [editando, setEditando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [form] = Form.useForm()

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    try {
      const [f, d] = await Promise.all([api('/trabajadores/me'), api('/trabajadores/me/documentos')])
      setFicha(f)
      setDocumentos(d)
    } catch (err) {
      message.error(err.message || 'Error cargando tu perfil')
    }
    setCargando(false)
  }

  const empezarEdicion = () => {
    form.setFieldsValue(ficha)
    setEditando(true)
  }

  const guardar = async (values) => {
    setGuardando(true)
    try {
      setFicha(await api('/trabajadores/me', { method: 'PUT', body: JSON.stringify(values) }))
      message.success('Datos guardados')
      setEditando(false)
    } catch (err) {
      message.error(err.message || 'Error guardando')
    }
    setGuardando(false)
  }

  const sinVer = documentos.filter(d => !d.visto).length
  const sinFirmar = documentos.filter(d => d.requiere_firma && !d.firmado).length

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <div style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)', padding: isMobile ? '14px 14px' : '20px' }}>
        <Title level={4} style={{ color: 'white', margin: 0 }}>Mi perfil</Title>
      </div>

      <div style={{ padding: '16px', maxWidth: 720, margin: '0 auto' }}>
        {cargando ? (
          <div style={{ textAlign: 'center', marginTop: '60px' }}><Spin size="large" /></div>
        ) : !ficha ? (
          <Empty description="Tu usuario no tiene ficha de trabajador. Si crees que es un error, avisa al administrador." style={{ marginTop: '60px' }} />
        ) : (
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            {sinFirmar > 0 && (
              <Alert type="warning" showIcon
                title={sinFirmar === 1 ? 'Tienes 1 documento pendiente de firmar' : `Tienes ${sinFirmar} documentos pendientes de firmar`}
                description="Pulsa «Ver y firmar». Cuando lo firmes podrás descargarlo." />
            )}
            <Card
              size="small"
              style={{ borderRadius: '12px' }}
              title={<><FileTextOutlined /> Mis documentos {sinVer > 0 && <Text type="danger" style={{ fontSize: '12px' }}>· {sinVer} sin abrir</Text>}</>}
            >
              <ListaDocumentos documentos={documentos} modo="propio" onCambio={cargar} />
            </Card>

            <Card
              size="small"
              style={{ borderRadius: '12px' }}
              title={<><UserOutlined /> {ficha.nombre} {ficha.apellidos}</>}
              extra={!editando && <Button size="small" icon={<EditOutlined />} onClick={empezarEdicion}>Cambiar mis datos</Button>}
            >
              {editando ? (
                <Form form={form} layout="vertical" onFinish={guardar}>
                  <Form.Item label="Teléfono" name="telefono"><Input /></Form.Item>
                  <Form.Item label="Correo personal" name="email_personal" rules={[{ type: 'email', message: 'Correo no válido' }]}><Input /></Form.Item>
                  <Form.Item label="Dirección" name="direccion"><Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} /></Form.Item>
                  <Form.Item label="Cuenta bancaria (IBAN)" name="iban"><Input placeholder="ES00 0000 0000 0000 0000 0000" /></Form.Item>
                  <Form.Item label="Contacto de emergencia" name="contacto_emergencia_nombre"><Input placeholder="Nombre" /></Form.Item>
                  <Form.Item label="Teléfono de emergencia" name="contacto_emergencia_telefono"><Input /></Form.Item>
                  <Form.Item label="Relación" name="contacto_emergencia_relacion"><Input placeholder="Hermano, pareja..." /></Form.Item>
                  <Space>
                    <Button onClick={() => setEditando(false)}>Cancelar</Button>
                    <Button type="primary" loading={guardando} onClick={() => form.submit()}>Guardar</Button>
                  </Space>
                </Form>
              ) : (
                <>
                  <Descriptions column={1} size="small" styles={{ label: { width: 150 } }}>
                    <Descriptions.Item label="Teléfono">{o(ficha.telefono)}</Descriptions.Item>
                    <Descriptions.Item label="Correo personal">{o(ficha.email_personal)}</Descriptions.Item>
                    <Descriptions.Item label="Dirección">{o(ficha.direccion)}</Descriptions.Item>
                    <Descriptions.Item label="Cuenta bancaria">{o(ficha.iban)}</Descriptions.Item>
                    <Descriptions.Item label="Contacto de emergencia">
                      {ficha.contacto_emergencia_nombre
                        ? `${ficha.contacto_emergencia_nombre}${ficha.contacto_emergencia_relacion ? ` (${ficha.contacto_emergencia_relacion})` : ''} · ${o(ficha.contacto_emergencia_telefono)}`
                        : '—'}
                    </Descriptions.Item>
                  </Descriptions>
                  <Descriptions column={1} size="small" styles={{ label: { width: 150 } }} style={{ marginTop: '10px' }}
                    title={<Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>Estos datos solo los puede cambiar el administrador</Text>}>
                    <Descriptions.Item label="DNI / NIE">{o(ficha.nif_nie)}</Descriptions.Item>
                    <Descriptions.Item label="Nº Seguridad Social">{o(ficha.naf)}</Descriptions.Item>
                    <Descriptions.Item label="Fecha de nacimiento">{fecha(ficha.fecha_nacimiento)}</Descriptions.Item>
                    <Descriptions.Item label="Puesto">{o(ficha.puesto)}</Descriptions.Item>
                    <Descriptions.Item label="Contrato">{o(ficha.tipo_contrato)}</Descriptions.Item>
                    <Descriptions.Item label="Fecha de alta">{fecha(ficha.fecha_alta)}</Descriptions.Item>
                  </Descriptions>
                </>
              )}
            </Card>
          </Space>
        )}
      </div>
    </div>
  )
}
